import type React from 'react'
import { InputWithSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'
import type { SpacingOption } from '../helpers/spacing-value'

const MANUAL_SPACING_SLIDER_MAX_POINTS = 50

interface SpacingAmountControlProps {
  disabled: boolean
  spacingOptions: SpacingOption[]
  selectedSpacing: string | null
  spaceBetween: number[]
  spacingInputValue: string
  spacingUnitSuffix: string
  onValueChange: (value: number[]) => void
  onInputChange: (value: string) => void
  onInputCommit: () => void
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

/** Manual spacing presets, slider (0-50pt) and typed value with unit suffix. */
export function SpacingAmountControl({
  disabled,
  spacingOptions,
  selectedSpacing,
  spaceBetween,
  spacingInputValue,
  spacingUnitSuffix,
  onValueChange,
  onInputChange,
  onInputCommit,
  onInputKeyDown,
}: SpacingAmountControlProps) {
  return (
    <div className="space-y-1.5">
      <ToggleGroup
        type="single"
        value={selectedSpacing || ''}
        disabled={disabled}
        onValueChange={(val) => {
          const option = spacingOptions.find((opt) => opt.label === val)

          if (option) {
            onValueChange([option.value])
          }
        }}
        className="justify-start gap-1 flex-wrap"
      >
        {spacingOptions.map(({ label }) => (
          <ToggleGroupItem
            key={label}
            value={label}
            aria-label={`Set spacing to ${label}`}
            className={cn(
              'h-8 min-w-0 rounded-xl border-0 bg-transparent px-2.5 py-0.5 text-xs font-medium shadow-none',
              'text-foreground/80 hover:bg-transparent hover:text-foreground',
              'data-[state=on]:bg-accent data-[state=on]:text-foreground',
              'focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:pointer-events-none disabled:opacity-40'
            )}
          >
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <div className="flex items-center gap-2">
        <Slider
          value={[Math.min(spaceBetween[0], MANUAL_SPACING_SLIDER_MAX_POINTS)]}
          disabled={disabled}
          onValueChange={(value) => onValueChange(value)}
          min={0}
          max={MANUAL_SPACING_SLIDER_MAX_POINTS}
          step={1}
          className="flex-1 min-w-0"
        />
        <InputWithSuffix
          type="text"
          inputMode="text"
          value={spacingInputValue}
          disabled={disabled}
          onKeyDown={onInputKeyDown}
          onChange={(e) => onInputChange(e.target.value)}
          onBlur={onInputCommit}
          size="compact"
          placeholder="Value"
          className="h-9 w-24 rounded-xl border-border/80 text-sm"
          suffix={
            spacingUnitSuffix ? (
              <span className="text-xs font-medium text-muted-foreground leading-none pr-0.5">
                {spacingUnitSuffix}
              </span>
            ) : undefined
          }
        />
      </div>
    </div>
  )
}
